import { Avatar, Icon, Rating, Text } from 'components';
import { TEXT_STRING } from '../../../constants';
import { getImageURL } from 'utils';
import { getSalonBadge } from 'components/Avatar/salonBadge';

type Props = {
  name?: string;
  avatar?: string;
  biography?: string | null;
  ranking?: number | null;
  isSalonMember?: boolean;
  isSalonPremiumMember?: boolean;
  onOpenRank?: () => void;
};

const COMMON_STR = TEXT_STRING.COMMON;
const MY_PAGE_STR = TEXT_STRING.MY_PAGE;

function MaleCard({
  name,
  avatar,
  biography = '',
  ranking,
  isSalonMember = false,
  isSalonPremiumMember = false,
  onOpenRank,
}: Props) {
  const isSalonStyle = isSalonMember || isSalonPremiumMember;
  const profileCardClassName = isSalonStyle
    ? 'border-[#E2BE63] bg-gradient-to-br from-[#FFFDF4] via-[#FFF4D3] to-[#FFE7AC]'
    : 'border-neutral-50 bg-white';
  const biographyClassName = isSalonStyle
    ? 'border-[#E9C76D] bg-white/80'
    : 'border-neutral-100 bg-neutral-25';
  const memberLabel = MY_PAGE_STR.SALON_MEMBER;
  const rankLabelColor = isSalonStyle ? 'text-[#B07C13]' : 'text-neutral-400';
  const nameColor = isSalonStyle ? 'text-[#7A4B00]' : 'text-neutral-900';

  return (
    <div className="flex flex-col gap-4">
      <div
        className={`relative rounded-3xl border shadow-lg p-8px ${profileCardClassName}`}
      >
        <div className="flex items-center gap-12px">
          {/* Avatar */}
          <div className="relative shrink-0">
            <div
              className={`rounded-full p-[2px] ${
                isSalonStyle
                  ? 'bg-gradient-to-r from-[#9B6A10] to-[#D6AA44]'
                  : 'bg-primary'
              }`}
            >
              <div className="rounded-full bg-white">
                <Avatar
                  url={getImageURL(avatar)}
                  circle
                  size="w-24"
                  className="border-[3px] border-white bg-white"
                  salonBadge={getSalonBadge({
                    isSalonMember,
                    isSalonPremiumMember,
                  })}
                />
              </div>
            </div>
          </div>

          {/* Right panel */}
          <div className="min-w-0 flex-1 flex flex-col gap-6px">
            {/* Row 1: rank label + stars */}
            <div className="flex items-center gap-6px">
              <Text fontSize={14} bold textColor={rankLabelColor} nowrap>
                {MY_PAGE_STR.VIP_RANK_LABEL}
              </Text>
              <Rating
                onClick={onOpenRank}
                value={ranking ?? undefined}
                isBlingStar
                size="w-[19px]"
                className="gap-[3px] cursor-pointer"
              />
            </div>

            {/* Row 2: name + member badge */}
            <div className="flex items-center gap-8px">
              <Text
                bold
                fontSize={18}
                className="truncate"
                textColor={nameColor}
              >
                {name || COMMON_STR.MY_PAGE}
              </Text>
              {isSalonStyle && (
                <div className="flex shrink-0 items-center gap-2px rounded-full border px-[10px] py-[5px] border-[#E1BA57] bg-[#FFF3C5]">
                  <Icon icon="crown" width={12} height={12} color="#fac515" />
                  <Text fontSize={10} bold textColor="text-[#8A5C00]" nowrap>
                    {memberLabel}
                  </Text>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Biography */}
        <div
          className={`mt-12px rounded-2xl border p-12px ${biographyClassName}`}
        >
          <Text
            fontSize={14}
            textColor={biography ? 'text-neutral-700' : 'text-neutral-400'}
          >
            {biography || MY_PAGE_STR.BIOGRAPHY_PLACEHOLDER}
          </Text>
        </div>
      </div>
    </div>
  );
}

export default MaleCard;
